import { InputCheckbox } from '@creditornot/cb-checkbox';
import { CaretRight, InfoCircle } from '@creditornot/cb-icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { InputSwitch } from '@creditornot/cb-switch';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { typographyCss } from 'common/typography';

type CategoryAccordionItemProps = {
  id: string;
  categoryName: string;
  checked?: boolean;
  partiallyChecked?: boolean;
  onCheckboxClick?: (selectAll: boolean) => void;
  includeNewItems?: boolean;
  onIncludeNewItemsClick?: (includeNewItems: boolean) => void;
  identationLevel?: number;
  expanded: boolean;
  children: React.ReactNode;
};

const AccordionSection = styled.div``;

const AccordionHeader = styled.div`
  border-block-end: 1px solid ${color.border};
`;

const CaretContainer = styled.div`
  flex-shrink: 0;
  border-radius: 12px;
  height: 24px;
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${color.bgSurface};
`;

const CaretIcon = styled(CaretRight)`
  height: 12px;
  width: 12px;
  fill: ${color.text};
  transition: transform 500ms cubic-bezier(0.16, 1, 0.3, 1);

  &[data-direction='down'] {
    transform: rotate(90deg);
  }

  [dir='rtl'] & {
    transform: rotate(180deg);

    &[data-direction='down'] {
      transform: rotate(90deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
  }
`;

const AccordionButton = styled.button<{ $identationLevel: number }>`
  background: transparent;
  padding-block: 16px;
  padding-inline-start: ${({ $identationLevel }) => ($identationLevel + 1) * 24}px;
  padding-inline-end: 16px;
  width: 100%;
  color: ${color.text};
  text-align: start;
  border: none;
  overflow: hidden;
  cursor: pointer;
`;

const MainHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderInfo = styled.div`
  margin-block-start: 4px;
  padding-block: 2px;
  color: ${color.textSubdued};
  display: flex;
  gap: 4px;
`;

const InfoIcon = styled(InfoCircle)`
  width: 12px;
  height: 12px;
  fill: ${color.textSubdued};
  margin-block: 2px;
`;

const Emphasized = styled.span`
  font-weight: 700;
`;

const IncludeNewItemsSwitchLabel = styled.label`
  margin-block-start: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SwitchLabelText = styled.span`
  ${typographyCss.Body3}
`;

const CategoryTitle = styled.div`
  ${typographyCss.Heading6}
  margin-inline-end: auto;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Collapse = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition:
    grid-template-rows 500ms cubic-bezier(0.16, 1, 0.3, 1),
    opacity 500ms cubic-bezier(0.16, 1, 0.3, 1);

  &[data-hidden='false'] {
    grid-template-rows: 1fr;
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
  }
`;

const CollapsedContent = styled.div`
  overflow: hidden;
`;

export const CategoryAccordionItem = ({
  id,
  categoryName,
  checked,
  partiallyChecked,
  onCheckboxClick,
  includeNewItems,
  onIncludeNewItemsClick,
  identationLevel = 0,
  expanded,
  children,
}: CategoryAccordionItemProps) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [renderCollapsedContent, setRenderCollapsedContent] = useState(isExpanded);

  // Parent component can control to open, but not close the accordion
  useEffect(() => {
    if (expanded) {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  const buttonId = `button-${id}`;
  const contentId = `content-${id}`;

  useEffect(() => {
    if (isExpanded && !renderCollapsedContent) {
      setRenderCollapsedContent(true);
    }
  }, [isExpanded, renderCollapsedContent]);

  return (
    <AccordionSection>
      <AccordionHeader>
        <AccordionButton
          type="button"
          aria-controls={contentId}
          aria-expanded={isExpanded}
          id={buttonId}
          onClick={() => setIsExpanded(!isExpanded)}
          $identationLevel={identationLevel}
        >
          <MainHeaderContent>
            {!!onCheckboxClick && (
              <InputCheckbox
                checked={checked}
                partiallyChecked={partiallyChecked}
                // Prevent accordion toggle on checkbox click
                onClick={event => event.stopPropagation()}
                onChange={() => {
                  onCheckboxClick(!checked && !partiallyChecked);
                }}
              />
            )}

            <CategoryTitle>{categoryName}</CategoryTitle>

            <CaretContainer>
              <CaretIcon data-direction={isExpanded ? 'down' : 'right'} />
            </CaretContainer>
          </MainHeaderContent>

          {checked !== false &&
            includeNewItems !== undefined &&
            (onIncludeNewItemsClick ? (
              <IncludeNewItemsSwitchLabel
                // Prevent accordion toggle on switch label click
                onClick={event => event.stopPropagation()}
              >
                <InputSwitch checked={includeNewItems} onChange={() => onIncludeNewItemsClick(!includeNewItems)} />
                <SwitchLabelText>
                  <FormattedMessage id="campaigns.form.item.include-new-category-items" />
                </SwitchLabelText>
              </IncludeNewItemsSwitchLabel>
            ) : (
              <HeaderInfo>
                <InfoIcon />
                {includeNewItems ? (
                  <FormattedMessage id="campaigns.form.item.category-items-will-be-included" tagName="span" />
                ) : (
                  <FormattedMessage
                    id="campaigns.form.item.category-items-will-not-be-included"
                    values={{
                      b: text => <Emphasized>{text}</Emphasized>,
                    }}
                    tagName="span"
                  />
                )}
              </HeaderInfo>
            ))}
        </AccordionButton>
      </AccordionHeader>
      <Collapse
        data-hidden={!isExpanded}
        onTransitionEnd={() => {
          if (!isExpanded) {
            setRenderCollapsedContent(false);
          }
        }}
      >
        {renderCollapsedContent && (
          <CollapsedContent aria-labelledby={buttonId} id={contentId}>
            {children}
          </CollapsedContent>
        )}
      </Collapse>
    </AccordionSection>
  );
};
